import type { SearchParams } from '@/server/searchParams.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { COOKIE_KEY } from '@/app/components/search/utils/Cookies/types.ts';

export enum ViewMode {
  MAP = 'map',
  LIST = 'list',
}

export type ViewModeByDevice = {
  desktop: ViewMode;
  mobile: ViewMode;
};

export type ListViewType = boolean | null;

// viewMode utils

const getInitialViewMode = ({
  cookie,
  isDesktop,
  localization,
  searchParams,
}: {
  cookie?: string;
  isDesktop: boolean;
  localization: Localization;
  searchParams: SearchParams;
}): ViewMode => {
  if (isDesktop) {
    const defaultViewModeInDesktop = localization?.configValuesApp?.defaultViewModeInDesktop;

    // 1. if cookie is present use it
    if (cookie) {
      if (cookie === ViewMode.MAP) return ViewMode.MAP;
      if (cookie === ViewMode.LIST) return ViewMode.LIST;
    }

    // 2. if search param is present use it
    if (searchParams[URLParameters.VIEW_MODE_DESKTOP]) {
      if (searchParams[URLParameters.VIEW_MODE_DESKTOP] === ViewMode.MAP) return ViewMode.MAP;
      if (searchParams[URLParameters.VIEW_MODE_DESKTOP] === ViewMode.LIST) return ViewMode.LIST;
    }

    // 3. if default is defined in CMS use it
    if (defaultViewModeInDesktop) {
      if (defaultViewModeInDesktop === ViewMode.MAP) return ViewMode.MAP;
      if (defaultViewModeInDesktop === ViewMode.LIST) return ViewMode.LIST;
    }

    // 4. use default
    return ViewMode.MAP;
  }

  // 6. use mobile default
  return ViewMode.LIST;
};

export const getInitialViewModes = ({
  desktopCookie,
  localization,
  searchParams,
}: {
  desktopCookie?: string;
  localization: Localization;
  searchParams: SearchParams;
}) => ({
  viewModeDesktopServer: getInitialViewMode({
    cookie: desktopCookie,
    isDesktop: true,
    localization,
    searchParams,
  }),
  viewModeMobileServer: getInitialViewMode({
    isDesktop: false,
    localization,
    searchParams,
  }),
});

export const getDesktopViewModeCookieKey = (localization: Localization): COOKIE_KEY => {
  const { evolutionResultsOnlyEnabled } = localization.configValuesApp;
  if (evolutionResultsOnlyEnabled) return COOKIE_KEY.LIST_VIEW_DESKTOP_EVOLUTION;
  return COOKIE_KEY.LIST_VIEW_DESKTOP;
};

export const getDefaultViewModeInDesktop = ({
  listAsDefault,
  cmsViewModeDefault,
}: {
  listAsDefault: boolean;
  cmsViewModeDefault: string;
}): string => {
  if (listAsDefault) return ViewMode.LIST;
  return cmsViewModeDefault;
};

// list view utils

export const isListView = (viewMode: ViewMode) => viewMode && viewMode === ViewMode.LIST;

export const getListViewFromUrlOrFallback = (fallback: ListViewType): ListViewType => {
  const currentSearchParams = getSearchParams();
  const viewModeFromUrl = currentSearchParams?.[URLParameters.VIEW_MODE_DESKTOP];
  return viewModeFromUrl ? viewModeFromUrl === ViewMode.LIST : fallback;
};

export const toggleListViewValue = (isDesktop: boolean, viewMode: ViewModeByDevice) => {
  const oldValue = isDesktop ? viewMode.desktop : viewMode.mobile;

  if (oldValue === ViewMode.LIST) {
    return ViewMode.MAP;
  }

  return ViewMode.LIST;
};
